/**
 * Striscia «Ultimi presi» in Durante asta: ultimi 7 (Rosa ∪ usciti).
 * Verde = preso da me (Rosa); giallo = preso da altri (uscite).
 * Ogni chip ha annullamento rapido (×).
 */
import { escapeHtml } from '../utils/escapeHtml.js';
import { parsePlayerKey } from '../utils/playerKey.js';

const RECENT_TAKES_LIMIT = 7;

/**
 * @param {unknown} meta
 * @param {number} [fallbackAt]
 */
export function normalizeTakeMeta(meta, fallbackAt = Date.now()) {
  const price = Number(meta?.price);
  const at = Number(meta?.at);
  return {
    price: Number.isFinite(price) ? price : 0,
    at: Number.isFinite(at) && at > 0 ? at : fallbackAt,
  };
}

export const recentTakesMethods = {
  /**
   * @returns {{ playerKey: string, name: string, price: number, at: number, side: 'me'|'others' }[]}
   */
  collectRecentTakes(limit = RECENT_TAKES_LIMIT) {
    const items = [];
    const roster = this.state.roster instanceof Map ? this.state.roster : null;
    const exits = this.state.auctionExits instanceof Map ? this.state.auctionExits : null;

    if (roster) {
      for (const [playerKey, meta] of roster) {
        if (!playerKey) continue;
        const { price, at } = normalizeTakeMeta(meta, 0);
        const parsed = parsePlayerKey(playerKey);
        items.push({
          playerKey,
          name: parsed?.name || playerKey,
          price,
          at,
          side: 'me',
        });
      }
    }
    if (exits) {
      for (const [playerKey, meta] of exits) {
        if (!playerKey) continue;
        if (roster?.has(playerKey)) continue;
        const { price, at } = normalizeTakeMeta(meta, 0);
        const parsed = parsePlayerKey(playerKey);
        items.push({
          playerKey,
          name: parsed?.name || playerKey,
          price,
          at,
          side: 'others',
        });
      }
    }

    items.sort((a, b) => (b.at - a.at) || a.name.localeCompare(b.name, 'it'));
    return items.slice(0, limit);
  },

  setupRecentTakesStrip() {
    const strip = document.getElementById('recent-takes-strip');
    if (!strip || strip.dataset.bound) return;
    strip.dataset.bound = '1';
    strip.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="undo-recent-take"]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      const playerKey = btn.dataset.playerKey || '';
      const side = btn.dataset.side === 'me' ? 'me' : 'others';
      if (!playerKey) return;
      void this.undoRecentTake?.(playerKey, side, btn);
    });
  },

  /**
   * Annulla un preso dalla striscia: torna in lista e restituisce i crediti.
   * @param {string} playerKey
   * @param {'me'|'others'} side
   * @param {HTMLButtonElement} [trigger]
   */
  async undoRecentTake(playerKey, side, trigger) {
    if (!playerKey) return;
    if (trigger) {
      trigger.disabled = true;
      trigger.setAttribute('aria-busy', 'true');
    }
    try {
      if (side === 'me') {
        await this.removeFromRoster?.(playerKey);
      } else {
        await this.unmarkAuctionExited?.(playerKey);
      }
    } catch (err) {
      console.error('[Ultimi presi] Annullamento non riuscito:', err);
      if (trigger) {
        trigger.disabled = false;
        trigger.removeAttribute('aria-busy');
      }
    }
  },

  renderRecentTakesStrip() {
    const strip = document.getElementById('recent-takes-strip');
    const track = strip?.querySelector('[data-testid="recent-takes-track"]');
    if (!strip || !track) return;

    const live = this.state.auctionMode === 'live';
    strip.hidden = !live;
    if (!live) {
      track.innerHTML = '';
      return;
    }

    const items = this.collectRecentTakes();
    if (!items.length) {
      track.innerHTML = `<span class="recent-takes-empty">Nessun preso ancora</span>`;
      return;
    }

    track.innerHTML = items
      .map((item) => {
        const priceLabel = Number.isFinite(item.price) && item.price > 0
          ? `${item.price}M`
          : '—';
        const sideClass = item.side === 'me'
          ? 'recent-takes-chip--me'
          : 'recent-takes-chip--others';
        const sideLabel = item.side === 'me' ? 'Preso da me' : 'Preso da altri';
        const undoLabel = item.side === 'me'
          ? 'Annulla presa — torna in lista e restituisce i crediti'
          : 'Annulla uscita — torna in lista';
        return `<span class="recent-takes-chip ${sideClass}" role="listitem" data-side="${item.side}" title="${escapeHtml(item.name)} · ${escapeHtml(priceLabel)} · ${sideLabel}">
          <span class="recent-takes-name">${escapeHtml(item.name)}</span>
          <span class="recent-takes-price num">${escapeHtml(priceLabel)}</span>
          <button type="button"
            class="recent-takes-undo"
            data-action="undo-recent-take"
            data-player-key="${escapeHtml(item.playerKey)}"
            data-side="${item.side}"
            title="${escapeHtml(undoLabel)}"
            aria-label="${escapeHtml(undoLabel)}"
            data-testid="recent-takes-undo-${escapeHtml(item.playerKey)}">×</button>
        </span>`;
      })
      .join('');
  },
};
